import React, { useState, useEffect, useRef, useCallback } from 'react';
import Select from 'react-select';
import Jspreadsheet from 'jspreadsheet-ce';
import './EditHistory.css';
import EditHistoryService from './EditHistoryService';
import CorgeeStorageHelper from '@/components/Globals/CorgeeStorageHelper';
import RenderInBody from '@components/Globals/RenderInBody';

interface CustomFieldProp {
  CoreTaskId: number;
  CustomFieldValueId?: number | null | string;
  CustomFieldId: number;
  FieldTypeId: number;
  LayerId: number;
  ProjectId?: number;
  PersonId?: number;
}

interface SelectOption {
  value: string;
  label: string;
}

interface EditDetailHistoryProps {
  customField: CustomFieldProp;
  open: boolean;
  api: any;
  title?: string;
  onClose?: () => void;
  onSaveModal?: (value: string) => void;
  height?: number;
}

const MONTH_OPTIONS: SelectOption[] = [
  { value: '0', label: 'January' },
  { value: '1', label: 'February' },
  { value: '2', label: 'March' },
  { value: '3', label: 'April' },
  { value: '4', label: 'May' },
  { value: '5', label: 'June' },
  { value: '6', label: 'July' },
  { value: '7', label: 'August' },
  { value: '8', label: 'September' },
  { value: '9', label: 'October' },
  { value: '10', label: 'November' },
  { value: '11', label: 'December' },
];

const getLoaderUrl = (): string =>
{
  const url = document.URL;
  if (url.indexOf('/Chat/') > 0) return '../../../Images/loader.gif';
  if (url.indexOf('ConversationDetails') > 0 || url.indexOf('chat') > 0)
    return '../../Images/loader.gif';
  return 'Images/loader.gif';
};

const fmt = (date: Date): string =>
  `${(date.getMonth() + 1).toString().padStart(2, '0')}/${date
    .getDate()
    .toString()
    .padStart(2, '0')}/${date.getFullYear()}`;

const getIsToday = (dateStr: string): boolean => fmt(new Date()) === dateStr;

const EditDetailHistory: React.FC<EditDetailHistoryProps> = ({
  customField,
  open,
  api,
  title = 'Edit Detail History',
  onClose,
  onSaveModal,
  height: _height = 800,
}) =>
{
  const [noData, setNoData] = useState(false);
  const [selectMonth, setSelectMonth] = useState(new Date().getMonth().toString());
  const [selectYear, setSelectYear] = useState(new Date().getFullYear());
  const [isSaving, setIsSaving] = useState(false);
  const [_dashboards, setDashboards] = useState<SelectOption[]>([]);
  const [selectDashboard, setSelectDashboard] = useState('');
  const [showErrorDashboard, setShowErrorDashboard] = useState(false);

  const wrapperRef = useRef<HTMLDivElement>(null);
  const spreadsheetRef = useRef<any>(null);
  const UpdateRowRef = useRef(true);
  const cancelUpdateRef = useRef(false);
  const lastValueRef = useRef<any>(0);

  // Drag state
  const [dragPos, setDragPos] = useState<{ x: number; y: number } | null>(null);
  const modalRef = useRef<HTMLDivElement>(null);
  const dragOrigin = useRef<{ startX: number; startY: number; posX: number; posY: number } | null>(
    null,
  );

  // Refs to keep live values accessible inside jspreadsheet callbacks
  const isSavingRef = useRef(false);
  const selectDashboardRef = useRef('');
  const customFieldRef = useRef(customField);
  const onSaveModalRef = useRef(onSaveModal);
  const titleRef = useRef(title);

  useEffect(() =>
  {
    isSavingRef.current = isSaving;
  }, [isSaving]);
  useEffect(() =>
  {
    selectDashboardRef.current = selectDashboard;
  }, [selectDashboard]);
  useEffect(() =>
  {
    customFieldRef.current = customField;
  }, [customField]);
  useEffect(() =>
  {
    onSaveModalRef.current = onSaveModal;
  }, [onSaveModal]);
  useEffect(() =>
  {
    titleRef.current = title;
  }, [title]);

  // Reset drag position each time the modal opens
  useEffect(() =>
  {
    if (!open) setDragPos(null);
  }, [open]);

  // Global mouse handlers for drag
  useEffect(() =>
  {
    const onMouseMove = (e: MouseEvent) =>
    {
      if (!dragOrigin.current) return;
      setDragPos({
        x: dragOrigin.current.posX + (e.clientX - dragOrigin.current.startX),
        y: dragOrigin.current.posY + (e.clientY - dragOrigin.current.startY),
      });
    };
    const onMouseUp = () =>
    {
      dragOrigin.current = null;
    };
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    return () =>
    {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
  }, []);

  const loaderUrl = getLoaderUrl();

  const getMonthOptions = useCallback((): SelectOption[] =>
  {
    const currentDate = new Date();
    if (currentDate.getFullYear() === selectYear)
    {
      return MONTH_OPTIONS.slice(0, currentDate.getMonth() + 1);
    }
    return MONTH_OPTIONS;
  }, [selectYear]);

  const loadData = useCallback(
    (startReal: string, endReal: string) =>
    {
      if (!selectDashboardRef.current)
      {
        setShowErrorDashboard(true);
        setNoData(true);
        return;
      }

      const cf = customFieldRef.current;
      const request = {
        api,
        CustomFieldValueId: (cf.CustomFieldValueId as number) ?? 0,
        CustomFieldId: cf.CustomFieldId,
        CoreTaskId: cf.CoreTaskId,
        Start: startReal,
        End: endReal,
      };

      EditHistoryService.getListCustomFieldHistories(request).then(datas =>
      {
        if (datas.length === 0)
        {
          setNoData(true);
          return;
        }

        const col = [
          { type: 'text', title: 'Date', width: 75, readOnly: true, name: 'CreatedAt' },
          { type: 'text', title: 'Value', width: 50, name: 'Value' },
          { type: 'text', title: 'Modified by', width: 100, readOnly: true, name: 'Name' },
          {
            type: 'hidden',
            title: 'CustomFieldDashboardId',
            width: 90,
            readOnly: true,
            name: 'CustomFieldDashboardId',
          },
          {
            type: 'hidden',
            title: 'CustomFieldId',
            width: 90,
            readOnly: true,
            name: 'CustomFieldId',
          },
          {
            type: 'hidden',
            title: 'CustomFieldValueId',
            width: 90,
            readOnly: true,
            name: 'CustomFieldValueId',
          },
          { type: 'hidden', title: 'CoreTaskId', width: 90, readOnly: true, name: 'CoreTaskId' },
          { type: 'hidden', title: 'Last', width: 90, readOnly: true, name: 'Last' },
        ];

        const data = datas.map(item => ({
          ...item,
          CreatedAt: fmt(new Date(item.CreatedAt.replace('Z', ''))),
        }));

        // jspreadsheet-ce v5: event handlers at SpreadsheetOptions level,
        // data/column options inside worksheets[]. Instance is WorksheetInstance directly.
        //
        // getRowData(y) returns this.options.data[y] — the original object when data was
        // provided as objects. Use named keys; convert to array for setRowData.
        const colNames = [
          'CreatedAt',
          'Value',
          'Name',
          'CustomFieldDashboardId',
          'CustomFieldId',
          'CustomFieldValueId',
          'CoreTaskId',
          'Last',
        ] as const;
        // jspreadsheet-ce v5 setData converts object rows to arrays internally,
        // so getRowData(y) returns an array. Convert back to a named object.
        const toRowObj = (rawArr: any[]): any =>
        {
          const obj: any = {};
          colNames.forEach((k, i) =>
          {
            obj[k] = rawArr[i];
          });
          return obj;
        };
        const rowToArray = (r: any): any[] => colNames.map(k => r[k] ?? null);

        const setBodyPointerEvents = (val: string) =>
        {
          if (wrapperRef.current?.parentElement)
          {
            wrapperRef.current.parentElement.style.pointerEvents = val;
          }
        };

        const newOptions: any = {
          contextMenu: false,
          onbeforechange: (el: any, _cell: any, xRaw: number, yRaw: number) =>
          {
            const x = Number(xRaw);
            const y = Number(yRaw);
            const instance = el.worksheets?.[0] ?? el;
            if (instance.getSelectedRows().length > 1 || instance.getSelectedColumns().length > 1)
            {
              cancelUpdateRef.current = true;
              lastValueRef.current = instance.getValueFromCoords(x, y);
            }
          },
          onchange: (el: any, _cell: any, xRaw: number, yRaw: number, value: string) =>
          {
            // In jspreadsheet-ce v5, top-level event callbacks receive the SpreadsheetInstance
            // as the first argument. WorksheetInstance methods (getRowData, setRowData, etc.)
            // live on worksheets[0], not on the SpreadsheetInstance.
            // jspreadsheet-ce v5 may pass x/y as strings — coerce to number for strict comparisons.
            const x = Number(xRaw);
            const y = Number(yRaw);
            const instance = el.worksheets?.[0] ?? el;
            setBodyPointerEvents('none');

            if (isSavingRef.current)
            {
              setBodyPointerEvents('');
              return;
            }

            // getRowData may return an array (v5 internal format) or the original object.
            const rawRow = instance.getRowData(y) ?? [];
            const rowData: any = Array.isArray(rawRow) ? toRowObj(rawRow) : { ...rawRow };
            const totalRows: number = instance.records?.length ?? 0;
            const newY = totalRows > 0 && totalRows - 1 === y ? y : y + 1;

            if (cancelUpdateRef.current)
            {
              cancelUpdateRef.current = false;
              UpdateRowRef.current = false;
              rowData.Value = lastValueRef.current;
              instance.ignoreEvents = true;
              instance.setRowData(y, rowToArray(rowData));
              instance.ignoreEvents = false;
              setBodyPointerEvents('');
              return;
            }

            instance.resetSelection();
            instance.ignoreEvents = true;

            const elementGrid = wrapperRef.current;
            if (!elementGrid)
            {
              instance.ignoreEvents = false;
              setBodyPointerEvents('');
              return;
            }

            // x === 1 is the 'Value' column (0-based: CreatedAt=0, Value=1)
            if (x !== 1)
            {
              instance.ignoreEvents = false;
              if (!UpdateRowRef.current) UpdateRowRef.current = true;
              setBodyPointerEvents('');
              return;
            }

            rowData.Value = value;
            const isToday = getIsToday(rowData.CreatedAt);
            const customFieldDashboardId: number = rowData.CustomFieldDashboardId ?? 0;
            const newValue = value;
            elementGrid.style.opacity = '0.3';
            isSavingRef.current = true;
            setIsSaving(true);

            // Resets all saving state — called after success or failure.
            const resetState = () =>
            {
              isSavingRef.current = false;
              setIsSaving(false);
              elementGrid.style.opacity = '1';
              instance.ignoreEvents = false;
              setBodyPointerEvents('');
            };

            // Validate numeric fields
            if (newValue !== '')
            {
              const currentCf = customFieldRef.current;
              if (currentCf.FieldTypeId === 3 && String(newValue).search(/^\d+(\.\d+)?$/) === -1)
              {
                rowData.Value = '';
                instance.ignoreEvents = true;
                instance.setRowData(y, rowToArray(rowData));
                resetState();
                return;
              }
              if (currentCf.FieldTypeId === 4 && String(newValue).search(/^\d+$/) === -1)
              {
                rowData.Value = '';
                instance.ignoreEvents = true;
                instance.setRowData(y, rowToArray(rowData));
                resetState();
                return;
              }
            }

            // Called after a successful NewCustomFieldHistories response.
            // notifyParent=true only for today saves — it triggers onSaveModal which
            // updates the current field value (api/customfieldvalue/). Past-date edits
            // must NOT call onSaveModal or the current value gets overwritten.
            const afterHistory = (resp: any, notifyParent: boolean = true) =>
            {
              if (resp)
              {
                rowData.Name = CorgeeStorageHelper.fullName();
                rowData.CustomFieldDashboardId = resp.CustomFieldDashboardId;
                if (resp.Last != null) rowData.Last = resp.Last;
                instance.ignoreEvents = true;
                instance.setRowData(y, rowToArray(rowData));
                // setRowData skips readonly cells in the DOM — force-refresh Name (col 2)
                instance.setValueFromCoords(2, y, rowData.Name ?? '', true);
                if (notifyParent) onSaveModalRef.current?.(newValue);
              }
              setTimeout(() =>
              {
                resetState();
                instance.updateSelectionFromCoords(x, newY, x, newY);
              }, 0);
            };

            const onError = () =>
            {
              // Restore original value on API failure so the grid is not left dirty.
              instance.ignoreEvents = true;
              instance.setRowData(y, rowToArray(rowData));
              instance.ignoreEvents = false;
              setTimeout(resetState, 0);
            };

            const makeCreatedAt = (): string =>
            {
              const create = new Date(rowData.CreatedAt);
              create.setHours(0, 0, 0, 0);
              return create.toISOString().replace('Z', '');
            };

            if (isToday)
            {
              const currentCf = customFieldRef.current;
              const fieldTitle = titleRef.current;
              const existingCfValueId = Number(currentCf.CustomFieldValueId) || 0;
              instance.resetSelection();

              const doNewHistories = (cfValueId: number) =>
                EditHistoryService.newCustomFieldHistories({
                  api,
                  request: {
                    CreatedAt: makeCreatedAt(),
                    CustomFieldDashboardId: customFieldDashboardId,
                    Value: newValue,
                    CustomFieldValueId: cfValueId,
                    CustomFieldId: currentCf.CustomFieldId,
                    CoreTaskId: currentCf.CoreTaskId,
                    DashboardId: selectDashboardRef.current,
                  },
                });

              if (existingCfValueId > 0)
              {
                // CustomFieldValueId exists — PUT is handled by the parent via onSaveModal.
                doNewHistories(existingCfValueId).then(afterHistory).catch(onError);
              }
              else
              {
                // No CustomFieldValueId yet — POST to create it first.
                EditHistoryService.savePost({
                  api,
                  request: {
                    CoreTaskID: currentCf.CoreTaskId,
                    ProjectId: currentCf.ProjectId ?? 0,
                    PersonId: currentCf.PersonId ?? 0,
                    CustomFieldId: currentCf.CustomFieldId,
                    CustomFieldValueId: 0,
                    Description: fieldTitle,
                    FieldTypeId: currentCf.FieldTypeId,
                    LayerId: currentCf.LayerId,
                    Name: fieldTitle,
                    Value: newValue,
                  },
                })
                  .then(saveResp =>
                  {
                    customFieldRef.current = {
                      ...customFieldRef.current,
                      CustomFieldValueId: saveResp.CustomFieldValueId,
                    };
                    return doNewHistories(saveResp.CustomFieldValueId);
                  })
                  .then(afterHistory)
                  .catch(onError);
              }
            }
            else if (customFieldDashboardId > 0)
            {
              // Past row with existing history entry — update it.
              instance.resetSelection();
              EditHistoryService.updateCustomFieldHistories({
                api,
                request: { CustomFieldDashboardId: customFieldDashboardId, Value: newValue },
              })
                .then(() =>
                {
                  rowData.Name = CorgeeStorageHelper.fullName();
                  instance.ignoreEvents = true;
                  instance.setRowData(y, rowToArray(rowData));
                  // setRowData skips readonly cells in the DOM — force-refresh Name (col 2)
                  instance.setValueFromCoords(2, y, rowData.Name ?? '', true);
                  instance.ignoreEvents = false;
                  setTimeout(() =>
                  {
                    resetState();
                    instance.updateSelectionFromCoords(x, newY, x, newY);
                  }, 0);
                })
                .catch(onError);
            }
            else
            {
              // Past row with no history entry yet — create one.
              const currentCf = customFieldRef.current;
              if (!currentCf.CustomFieldValueId)
              {
                UpdateRowRef.current = false;
                alert('Please log the hours backwards, starting from the current day.');
                rowData.Value = '';
                instance.ignoreEvents = true;
                instance.setRowData(y, rowToArray(rowData));
                resetState();
                return;
              }

              instance.resetSelection();
              EditHistoryService.newCustomFieldHistories({
                api,
                request: {
                  CreatedAt: makeCreatedAt(),
                  CustomFieldDashboardId: customFieldDashboardId,
                  Value: newValue,
                  CustomFieldValueId: currentCf.CustomFieldValueId as number,
                  CustomFieldId: currentCf.CustomFieldId,
                  CoreTaskId: currentCf.CoreTaskId,
                  DashboardId: selectDashboardRef.current,
                },
              })
                .then(resp => afterHistory(resp, false))
                .catch(onError);
            }

            if (!UpdateRowRef.current)
            {
              UpdateRowRef.current = true;
            }
          },
          worksheets: [
            {
              columnDrag: false,
              data,
              columns: col,
              columnResize: false,
              rowResize: false,
              rowDrag: false,
              columnSorting: false,
              allowInsertRow: false,
            },
          ],
        };

        setNoData(false);

        setTimeout(() =>
        {
          if (wrapperRef.current)
          {
            Jspreadsheet.destroy(wrapperRef.current as any, false);
            spreadsheetRef.current = Jspreadsheet(wrapperRef.current as any, newOptions);
            const ws = spreadsheetRef.current.worksheets?.[0] ?? spreadsheetRef.current;
            ws.hideIndex?.();

            // Inject a persistent <style> into the wrapper so hidden columns
            // stay hidden even after jspreadsheet redraws cells.
            // Columns 3-7 (0-based data-x) must be invisible.
            // The row-index td has no data-x, so data-x="0" = Date, "1" = Value, etc.
            if (!wrapperRef.current.querySelector('style.eh-hidden-cols'))
            {
              const style = document.createElement('style');
              style.className = 'eh-hidden-cols';
              style.textContent =
                '[data-x="3"],[data-x="4"],[data-x="5"],[data-x="6"],[data-x="7"],[data-x="8"]' +
                '{ display:none !important; width:0 !important; }';
              wrapperRef.current.appendChild(style);
            }

            // Also hide by header text (covers the thead corner/index cell and any
            // header that still renders despite the data-x rule).
            const visibleTitles = new Set(['Date', 'Value', 'Modified by']);
            wrapperRef.current.querySelectorAll<HTMLElement>('thead td').forEach(th =>
            {
              if (!visibleTitles.has(th.textContent?.trim() ?? ''))
              {
                th.style.setProperty('display', 'none', 'important');
              }
            });

            // Fallback: hide body cells by position in case data-x is absent on td elements.
            // jspreadsheet tbody row: cells[0]=row-index, cells[1..]=data cols 0..
            // Hidden data cols 3-7 → cells[4..8]
            wrapperRef.current.querySelectorAll<HTMLElement>('tbody tr').forEach(row =>
            {
              const cells = row.querySelectorAll<HTMLElement>('td');
              // cells[0]=select-all, cells[1..3]=visible cols (Date/Value/Modified by),
              // cells[4..9]=hidden cols (data-x 3..8 including jspreadsheet's trailing "I" column)
              for (let i = 4; i <= 9; i++)
              {
                if (cells[i]) cells[i]!.style.setProperty('display', 'none', 'important');
              }
            });

            // Hide <col> elements in <colgroup> for hidden columns.
            // col[0]=select-all, col[1]=Date, col[2]=Value, col[3]=Modified by → visible.
            // col[4] onwards: data columns 3-7 (jspreadsheet sets display:none for type:'hidden')
            // plus the extra trailing "I" col that jspreadsheet appends — hide them all.
            const colgroup = wrapperRef.current.querySelector<HTMLElement>('colgroup');
            if (colgroup)
            {
              colgroup.querySelectorAll<HTMLElement>('col').forEach((col, i) =>
              {
                if (i >= 4) col.style.setProperty('display', 'none', 'important');
              });
            }
          }
        }, 0);
      });
    },
    [api],
  );

  // Load dashboards when modal opens
  useEffect(() =>
  {
    if (!open) return;

    setNoData(false);
    setShowErrorDashboard(false);
    setIsSaving(false);

    EditHistoryService.getDashboardsByCoreTaskId({
      api,
      CoreTaskId: customField.CoreTaskId,
    }).then(data =>
    {
      const opts: SelectOption[] = data.map(item => ({
        value: item.DashboardId,
        label: item.Name,
      }));
      setDashboards(opts);

      const firstId = data.length > 0 ? data[0]!.DashboardId : '';
      setSelectDashboard(firstId);
      selectDashboardRef.current = firstId;

      const now = new Date();
      setSelectMonth(now.getMonth().toString());
      setSelectYear(now.getFullYear());

      const start = new Date(now.getFullYear(), now.getMonth(), 1);
      const startReal = fmt(start);
      const endReal = fmt(now);

      if (firstId)
      {
        loadData(startReal, endReal);
      }
      else
      {
        setShowErrorDashboard(true);
        setNoData(true);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Reset spreadsheet when customField prop changes (from parent updates after save)
  useEffect(() =>
  {
    if (wrapperRef.current)
    {
      wrapperRef.current.style.opacity = '1';
    }
    isSavingRef.current = false;
    setIsSaving(false);
  }, [customField]);

  const handleSearch = useCallback(() =>
  {
    const now = new Date();
    const start = new Date(selectYear, parseInt(selectMonth), 1);

    if (now.getMonth() === parseInt(selectMonth) && now.getFullYear() === selectYear)
    {
      const startReal = fmt(start);
      const endReal = fmt(now);
      loadData(startReal, endReal);
    }
    else
    {
      const end = new Date(selectYear, parseInt(selectMonth) + 1, 0);
      const startReal = fmt(start);
      const endReal = fmt(end);
      loadData(startReal, endReal);
    }
  }, [selectMonth, selectYear, loadData]);

  const stepUpYear = useCallback(() =>
  {
    setSelectYear(prev => Math.min(prev + 1, new Date().getFullYear()));
  }, []);

  const stepDownYear = useCallback(() =>
  {
    setSelectYear(prev => Math.max(prev - 1, 2000));
  }, []);

  const handleHeaderMouseDown = useCallback((e: React.MouseEvent<HTMLDivElement>) =>
  {
    if (!modalRef.current) return;
    const rect = modalRef.current.getBoundingClientRect();
    dragOrigin.current = { startX: e.clientX, startY: e.clientY, posX: rect.left, posY: rect.top };
    e.preventDefault();
  }, []);

  if (!open) return null;

  const monthOptions = getMonthOptions();
  const modalStyle: React.CSSProperties = dragPos
    ? { width: '460px', position: 'fixed', left: dragPos.x, top: dragPos.y, margin: 0 }
    : { width: '460px' };

  return (
    <RenderInBody>
      <div className="edit-history corgee_com_modal-backdrop" onClick={onClose}>
        <div
          ref={modalRef}
          className="corgee_com_conversation-metadata-modal"
          style={modalStyle}
          onClick={e => e.stopPropagation()}
        >
          <div
            className="corgee_com_global-modal_header edit-history-drag-handle"
            onMouseDown={handleHeaderMouseDown}
          >
            <div className="header-info">
              <div className="react-add-new-card-title">{title}</div>
              <button className="close-control" onClick={onClose}>
                <i className="icon-incorrect-icon"></i>
              </button>
            </div>
          </div>
          <div>
            <p className="titleEditHistory">
              *Please log the hours backwards, starting from the current day.
            </p>
          </div>
          <div
            className="corgee_com_global-modal_body"
            style={{ paddingBottom: '10px', overflowX: 'auto' }}
          >
            <div id="editHistoryDetail" className="containerHistory">
              <div className="headerHistory">
                <div className="rowh">
                  <div className="pad col-xs-3">
                    <div className="cmbSelectMonthEdit">
                      <Select
                        classNamePrefix="eh-select"
                        value={monthOptions.find(opt => opt.value === selectMonth) ?? null}
                        options={monthOptions}
                        onChange={opt => opt && setSelectMonth(opt.value)}
                      />
                    </div>
                  </div>
                  <div className="pad col-xs-3">
                    <div className="quantity">
                      <input
                        id="inputYear"
                        name="inputYear"
                        type="number"
                        min="2000"
                        max="2100"
                        value={selectYear}
                        onChange={e =>
                        {
                          const val = parseInt(e.target.value);
                          setSelectYear(
                            val > new Date().getFullYear() ? new Date().getFullYear() : val,
                          );
                        }}
                      />
                      <div className="quantity-nav">
                        <div className="quantity-button quantity-up" onClick={stepUpYear}>
                          ▲
                        </div>
                        <div className="quantity-button quantity-down" onClick={stepDownYear}>
                          ▼
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="pad col-xs-2"></div>
                  <div className="pad col-xs-4">
                    <input
                      id="btnSearch"
                      name="btnSearch"
                      type="button"
                      value="Search"
                      onClick={handleSearch}
                    />
                  </div>
                </div>
              </div>

              <div className="bodyHistory">
                {noData ? (
                  <div style={{ width: '385px', paddingTop: '50px', textAlign: 'center' }}>
                    {showErrorDashboard
                      ? "You don't have the card on a dashboard. Cards in dynamic column do not apply."
                      : ''}
                  </div>
                ) : (
                  <>
                    {isSaving && (
                      <img className="loaderImageDetail" src={loaderUrl} alt="Loading..." />
                    )}
                    <div ref={wrapperRef} id="wrapper" />
                  </>
                )}
              </div>
            </div>
          </div>

          <div className="corgee_com_global-modal_footer">
            <input className="btn-close-history" type="button" value="Close" onClick={onClose} />
          </div>
        </div>
      </div>
    </RenderInBody>
  );
};

export default EditDetailHistory;
